import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HOC from '../../components/HOC/HOC';
import { getApi, postApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import img2 from '../../assest/loading1.gif';
import { formatDateTimeP } from '../../utils/utils';
import { formatCurrency } from '../Wallet Management/staffWalletDateUtils';
import PayoutConfirmModal from '../Manual Payouts/PayoutConfirmModal';
import pageStyles from './Payments.module.css';
import {
  canRefundPayout,
  getAccountNo,
  getCustomerName,
  getIfsc,
  getPaymentMode,
  getPerson,
  getRefundAmount,
  getStaffName,
  getStatus,
} from './paymentUtils';

const statusClass = status => {
  const value = String(status || '').toLowerCase();
  if (value === 'paid') return pageStyles.paid;
  if (value === 'pending') return pageStyles.pending;
  if (value === 'failed') return pageStyles.failed;
  return pageStyles.neutral;
};

const Field = ({ label, value }) => (
  <div className={pageStyles.field}>
    <span>{label}</span>
    <p>{value || '-'}</p>
  </div>
);

const PaymentsDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const fetchTransaction = useCallback(async () => {
    await getApi(endPoints.getPaymentbyid(id), {
      setResponse: response => setTransaction(response?.data || null),
      setLoading,
      errorMsg: 'Failed to fetch payment details!',
    });
  }, [id]);

  useEffect(() => {
    fetchTransaction();
  }, [fetchTransaction]);

  const handleRefund = async () => {
    const response = await postApi(
      endPoints.refundPendingTransaction,
      { transactionId: id },
      {
        setLoading: setRefunding,
        successMsg: 'Payout marked failed and the amount was refunded to the wallet.',
        errorMsg: 'Failed to refund payout!',
      }
    );

    setConfirmRefund(false);
    if (!response) return;
    fetchTransaction();
  };

  const person = getPerson(transaction);
  const txnStatus = getStatus(transaction);
  const refundAmount = getRefundAmount(transaction);
  const showRefund = canRefundPayout(transaction);

  return (
    <>
      <PayoutConfirmModal
        show={confirmRefund}
        title="Refund this payout?"
        message={`Refund ${formatCurrency(transaction?.amount)} for ${getStaffName(transaction)}. This marks the payout Failed and credits the same amount back to the staff wallet.`}
        confirmLabel="Refund amount"
        loading={refunding}
        onClose={() => setConfirmRefund(false)}
        onConfirm={handleRefund}
      />
      <div className={pageStyles.page}>
        <div className={pageStyles.header}>
          <div className={pageStyles.headerLeft}>
            <h6>Payment details</h6>
            <p>{transaction?.id || id}</p>
          </div>
          <div className={pageStyles.headerActions}>
            {showRefund ? (
              <button
                type="button"
                className={pageStyles.refundButton}
                onClick={() => setConfirmRefund(true)}
              >
                Refund {formatCurrency(transaction?.amount)}
              </button>
            ) : null}
            <button type="button" className={pageStyles.backButton} onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>

        {loading ? (
          <div className={pageStyles.loadingCell}>
            <img src={img2} alt="Loading" width={100} height={100} />
          </div>
        ) : (
          <>
            <div className={pageStyles.summary}>
              <div className={pageStyles.summaryItem}>
                <span>Amount</span>
                <strong>{formatCurrency(transaction?.amount)}</strong>
              </div>
              <div className={pageStyles.summaryItem}>
                <span>Status</span>
                <strong>
                  <span className={`${pageStyles.badge} ${statusClass(txnStatus)}`}>{txnStatus}</span>
                </strong>
              </div>
              <div className={pageStyles.summaryItem}>
                <span>Type</span>
                <strong>{transaction?.type || '-'}</strong>
              </div>
              <div className={pageStyles.summaryItem}>
                <span>Payment mode</span>
                <strong>{getPaymentMode(transaction)}</strong>
              </div>
            </div>

            <div className={pageStyles.detailGrid}>
              <div className={pageStyles.card}>
                <h6>Transaction</h6>
                <Field label="Transaction ID" value={transaction?.id || transaction?._id} />
                <Field
                  label="Date"
                  value={formatDateTimeP(transaction?.date || transaction?.createdAt)}
                />
                <Field label="Description" value={transaction?.description} />
                <Field label="Created by" value={transaction?.createdBy} />
                <Field
                  label="Refund amount"
                  value={refundAmount != null ? formatCurrency(refundAmount) : '-'}
                />
                <Field label="Remarks" value={transaction?.remarks} />
              </div>

              <div className={pageStyles.card}>
                <h6>Staff</h6>
                <Field label="Name" value={getStaffName(transaction)} />
                <Field label="Email" value={person.email} />
                <Field label="Phone" value={person.phone} />
                <Field label="Account no." value={getAccountNo(transaction)} />
                <Field label="IFSC" value={getIfsc(transaction)} />
                <Field label="Site / user" value={getCustomerName(transaction)} />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default HOC(PaymentsDetails);
